import { Box, Flex, Image, Text } from '@theme-ui/components';
import { Alert, Markdown } from '~/Common';
import { Field } from '~/Forms/Field';
import { Form, Formik } from 'formik';
import { Submit } from '~/Forms/Submit';
import { Yup } from 'utils/validationRules';
import { Citations } from '~/Deals/Citations';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { askLiquidityDealChat } from 'features/deal/dealSlice';
import { Icon } from 'assets/Icon';

const HEADER_H = 52;

const smoothScrollToBottom = (el, duration = 900) => {
  if (!el) return;
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const target = el.scrollHeight - el.clientHeight;
  if (prefersReduced) { el.scrollTop = target; return; }

  const start = el.scrollTop;
  const change = target - start;
  if (Math.abs(change) < 2) return;

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const startTime = performance.now();
  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / duration);
    el.scrollTop = start + change * easeInOutCubic(progress);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

const AnimatedEllipsis = () => {
    const [dots, setDots] = useState('');
    useEffect(() => {
        const interval = setInterval(() => {
            setDots(prev => (prev.length < 3 ? prev + '.' : ''));
        }, 500);
        return () => clearInterval(interval);
    }, []);
    return <>{dots}</>;
};

const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), ms);
    promise.then(
      (v) => { clearTimeout(id); resolve(v); },
      (e) => { clearTimeout(id); reject(e); }
    );
  });

export const AiQa = ({ deal }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [chatQA, setChatQA] = useState([]);
  const [apiError, setApiError] = useState(null);

  // refs for auto-scroll behavior
  const scrollRef = useRef(null);
  const shouldAutoScrollRef = useRef(false);

  // whenever chat grows AND we flagged an event, scroll
  useEffect(() => {
    if (!scrollRef.current) return;
    if (!shouldAutoScrollRef.current) return;
    smoothScrollToBottom(scrollRef.current, 950);
    shouldAutoScrollRef.current = false;
  }, [chatQA.length]);

  // when expanding, scroll to bottom after layout
  useEffect(() => {
    if (!expanded) return;
    const id = setTimeout(() => smoothScrollToBottom(scrollRef.current, 750), 0);
    return () => clearTimeout(id);
  }, [expanded]);

  return (
    <Box sx={{ bg: '#00000a', borderRadius: 12, pt: 2, pb: expanded ? 0 : 2 }}>
      <Box sx={{ px: 3 }}>
        <Flex
          role="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            height: HEADER_H,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Image src='/img/lqd-ai-logo.svg' width={37} />
          <Text sx={{ fontSize: 1, opacity: 0.75, color: 'white' }}>{expanded ? t('Hide') : t('Ask a Question')}</Text>
        </Flex>
      </Box>

      {expanded && (
        <Box
          sx={{
            bg: '#00000a',
            borderRadius: '0 0 18px 18px',
            boxShadow: 'card',
            mt: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 620,
          }}
        >
          {apiError && (
            <Alert role="alert" variant="danger" mb={0} sx={{ borderRadius: 0 }}>
              {apiError}
            </Alert>
          )}

          <Formik
            enableReinitialize
            initialValues={{ liquidityChat: '' }}
            validationSchema={Yup.object().shape({
              liquidityChat: Yup.string().max(500, t('Please limit your question to 500 characters')),
            })}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              setApiError(null);
              const q = (values.liquidityChat || '').trim();
              if (!q) return;

              const next = [...chatQA, { type: 'question', text: q }];
              setChatQA(next);
              shouldAutoScrollRef.current = true;
              setSubmitting(true);

              const REQUEST_TIMEOUT_MS = 60_000;

              try {
                const resp = await withTimeout(
                  askLiquidityDealChat(deal?.uuid, { question: q }),
                  REQUEST_TIMEOUT_MS
                );
                const failed = resp?.data?.status === 'failed';
                const a = failed
                  ? t('There was an error sending your question. Please try again later.')
                  : resp?.data?.answer;
                const c = resp?.data?.citations ?? [];
                setChatQA([...next, { type: 'answer', text: a, citations: c }]);
                shouldAutoScrollRef.current = true;
                resetForm();
              } catch (err) {
                const timedOut = err?.message === 'REQUEST_TIMEOUT';
                const msg = timedOut
                  ? t('The request took too long and timed out. Please try again or refine your question.')
                  : (err?.response?.data?.message ||
                    t('There was an error sending your question. Please try again later.'));
                setApiError(msg);
                setChatQA((prev) => [...prev, { type: 'answer', text: msg }]);
                shouldAutoScrollRef.current = true;
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
              <Form
                id="deal-qa-form"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                style={{ display: 'contents' }}
              >
                {/* SCROLLABLE Q&A */}
                <Box
                  ref={scrollRef}
                  sx={{ 
                    flex: '1 1 auto',
                    overflowY: 'auto', px: 3, py: chatQA.length > 0 ? 3 : 0,
                    borderTop: expanded && chatQA.length ? '1px solid #D5D9DA' : 'none',
                    borderRadius: expanded && chatQA.length ? '8px 8px 0 0' : 'none',
                  }}
                >
                  {chatQA.map((qa, i) => {
                    const isQ = qa.type === 'question';
                    return (
                      <Flex key={i} sx={{ mb: 3, justifyContent: isQ ? 'flex-end' : 'flex-start' }}>
                        <Box
                          sx={{
                            maxWidth: '85%',
                            width: isQ ? ['85%', '70%', '60%'] : '100%',
                            p: 3,
                            borderRadius: 12,
                            bg: isQ ? 'tealLighter' : '#fefefe',
                            boxShadow: isQ ? 'none' : 'card',
                          }}
                        >
                          <Markdown as="div" color="black" sx={{ fontSize: '14px' }} className="ai-markdown">
                            {qa.text}
                          </Markdown>
                          {qa.type === 'answer' && qa.citations?.length ? (
                            <Citations citations={qa.citations} defaultOpen={false} />
                          ) : null}
                        </Box>
                      </Flex>
                    );
                  })}
                </Box>

                {isSubmitting && (
                <Box sx={{ m: 3 }}>
                    <Text as="p" variant="small" color="muted">
                    {t('Thinking')}<AnimatedEllipsis />
                    </Text>
                </Box>
                )}

                {/* FROZEN INPUT */}
                <Box
                  sx={{
                    flex: '0 0 auto',
                    px: 3,
                    pt: 2,
                    position: 'sticky',
                    bottom: 0,
                    bg: '#00000a',
                  }}
                >
                  <Flex sx={{ alignItems: 'center', gap: 2, border: '1px solid white', borderRadius: '8px', mt: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Field
                        name="liquidityChat"
                        placeholder={t('Ask a question about this deal')}
                        showLabel={false}
                        groupSx={{ pt: 0, mb: 0, fontSize: '14px' }}
                        variant="forms.input.dealQa"
                        disabled={isSubmitting}
                      />
                    </Box>
                    <Submit
                        form="deal-qa-form"
                        variant="primary"
                        type="submit"
                        forceSubmit
                        showErrorTooltip
                        disabled={isSubmitting}
                        sx={{ borderRadius: '999px', display: 'flex', p: 1, bg: 'white', mr: 2 }}
                        submittingText={<Icon icon="arrow-right" color="black" size="14" sx={{ transform: 'rotate(270deg)' }} />}
                    >
                        <Icon icon="arrow-right" color="black" size="14" sx={{ transform: 'rotate(270deg)' }} />
                    </Submit>
                  </Flex>
                  <Flex>
                    <Text as="p" sx={{ mx: "auto", my: 2 }} variant="smaller" color="placeholder">
                      {t('AI can make mistakes. Check important info.')}
                    </Text>
                  </Flex>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      )}
    </Box>
  );
};
